import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import Snackbar from '@material-ui/core/Snackbar';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/DeleteOutlineOutlined';
import LinkOutlined from '@material-ui/icons/LinkOutlined';
import DraftsTwoTone from '@material-ui/icons/DraftsTwoTone';
import DoneOutline from '@material-ui/icons/DoneOutline';
import Visibility from '@material-ui/icons/Visibility';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import MoreVert from '@material-ui/icons/MoreVert';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { Row } from 'components/UI/Row';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import { PageIndex } from 'components/UI/PageIndex';
import { TableLoader } from 'components/Loaders/TableLoader';
import { Replay } from '@material-ui/icons';
import { Dialog, DialogTitle, DialogActions } from '@material-ui/core';
import {
  fetchOrdersList,
  deleteOrderList,
  getOrdersList,
  getOrdersListLoaders,
  getOrdersListApiErrors,
  getOrdersListPagination,
  resetOrdersList,
  closeError,
  reopenOrderList
} from './ordersList';

export const CustomerOrdersList = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const ordersList = useSelector(getOrdersList);
  const pagination = useSelector(getOrdersListPagination);
  const { customerId } = useParams<{ customerId: string }>();
  const { isLoading, isDeletingItems } = useSelector(getOrdersListLoaders);
  const { loadingError, deletingError } = useSelector(getOrdersListApiErrors);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [deleteOrderListId, setDeleteOrderListId] = useState<string | undefined>(undefined);

  useEffect(() => {
    dispatch(fetchOrdersList({ customer_id: customerId, index: pagination.index ?? 1 }));
    return () => {
      dispatch(resetOrdersList());
    };
  }, []);

  const cancelDeleteOrderList = () => {
    setDeleteOrderListId(undefined);
  };

  const confirmDelete = () => {
    if (deleteOrderListId) {
      dispatch(deleteOrderList(deleteOrderListId));
    }
    cancelDeleteOrderList();
  };

  const askDeleteOrderList = (id: string) => {
    setDeleteOrderListId(id);
  };

  if (loadingError) {
    return (
      <>
        <Row>
          <strong>{t('customers.orders lists')}</strong>
        </Row>
        <Alert severity="error">{loadingError}</Alert>
      </>
    );
  }

  return (
    <>
      <Row justifyContent="space-between" margin="0 0 20px 0">
        <strong>{t('customers.orders lists')}</strong>
        <Button
          color="primary"
          variant="contained"
          onClick={() => {
            history.push(`/customers/details/${customerId}/orderlist/new`);
          }}
        >
          {t('orderList.new')}
        </Button>
      </Row>
      <TableContainer component={Paper} elevation={3} ref={tableRef}>
        <Table stickyHeader aria-label="order list">
          <TableHead>
            <TableRow>
              <TableCell align="center">{t('label.status')}</TableCell>
              <TableCell>{t('label.short name')}</TableCell>
              <TableCell>{t('label.approved on')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && <TableLoader colSpan={4} />}
            {!isLoading && isEmpty(ordersList) && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Row height="50vh" justifyContent="center" alignItems="center">
                    {t('orderList.empty list')}
                  </Row>
                </TableCell>
              </TableRow>
            )}
            {!isLoading && (
              <>
                {map(ordersList, (orderList: any, index: number) => (
                  <TableRow key={`user_${index}`}>
                    <TableCell align="center">
                      {orderList?.approved_at ? <DoneOutline style={{ color: '#00bf10' }} /> : <DraftsTwoTone />}
                    </TableCell>
                    <TableCell>{orderList?.short_name}</TableCell>
                    <TableCell>{orderList?.approved_at}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('orderList.add products and serial numbers') || ''} arrow>
                        <span>
                          {orderList.approved_at && (
                            <IconButton
                              aria-label="reopen-order-list"
                              color="primary"
                              onClick={() => {
                                dispatch(
                                  reopenOrderList({
                                    id: orderList.id,
                                    currentIndex: pagination.index,
                                    customerId
                                  })
                                );
                              }}
                            >
                              <Replay />
                            </IconButton>
                          )}
                          {!orderList.approved_at && (
                            <IconButton
                              aria-label="link-products"
                              color="primary"
                              onClick={() => {
                                history.push(`/customers/details/${customerId}/orderlist/${orderList.id}`);
                              }}
                            >
                              <LinkOutlined />
                            </IconButton>
                          )}
                        </span>
                      </Tooltip>
                      <Tooltip title={t('orderList.review and approve') || ''} arrow>
                        <IconButton
                          aria-label="finalize"
                          color="primary"
                          onClick={() => {
                            history.push(`/customers/details/${customerId}/orderlist/finalize/${orderList.id}`);
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        aria-label="delete"
                        color="secondary"
                        disabled={orderList?.approved_at || (isDeletingItems && isDeletingItems[orderList.id])}
                        onClick={() => {
                          askDeleteOrderList(orderList.id);
                        }}
                      >
                        <DeleteIcon />
                        {isDeletingItems && isDeletingItems[orderList.id] && (
                          <CircularProgress
                            color="secondary"
                            size={34}
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              marginTop: -17,
                              marginLeft: -17
                            }}
                          />
                        )}
                      </IconButton>
                      {/*  <IconButton
                      aria-controls="serial-numbers-menu"
                      aria-haspopup="true"
                      onClick={e => setMenuAnchor(e.currentTarget)}
                      disabled={!orderList?.approved_at}
                    >
                      <MoreVert />
                    </IconButton>
                    <Menu
                      id="serial-numbers-menu"
                      anchorEl={menuAnchor}
                      keepMounted
                      open={Boolean(menuAnchor)}
                      onClose={() => setMenuAnchor(null)}
                    >
                      <MenuItem
                        onClick={() => {
                          history.push(`/customers/details/${customerId}/orderlist/testreport/${orderList.id}`);
                        }}
                      >
                        {t('label.test report')}
                      </MenuItem>
                    </Menu> */}
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <PageIndex
        margin="25px 0"
        justifyContent="center"
        index={pagination.index}
        indexesCount={pagination.indexesCount || 0}
        onChange={async (_, page: number) => {
          await dispatch(fetchOrdersList({ customer_id: customerId, index: page }));
          if (tableRef) {
            tableRef?.current?.scrollIntoView();
          }
        }}
      />

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        key="bottom,center"
        open={!isEmpty(deletingError)}
        autoHideDuration={5000}
        onClose={() => {
          dispatch(closeError());
        }}
      >
        <Alert severity="error" variant="filled" onClose={() => dispatch(closeError())}>
          {deletingError}
        </Alert>
      </Snackbar>

      <Dialog open={deleteOrderListId !== undefined} onClose={() => setDeleteOrderListId(undefined)}>
        <DialogTitle>{t('orderList.confirm delete')}</DialogTitle>
        <DialogActions>
          <Button color="primary" onClick={confirmDelete}>
            {t('common.ok')}
          </Button>
          <Button color="secondary" onClick={cancelDeleteOrderList}>
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
